import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FileText, Scale, FileSignature, DollarSign, CheckCircle2 } from 'lucide-react';
export function OfferToDepositPage() {
  const steps = [{
    title: 'Capture the offer',
    desc: 'Store terms in structured fields, not just a thread.',
    icon: FileText
  }, {
    title: 'Compare to your rules',
    desc: 'See what matches, what does not, and what needs approval.',
    icon: Scale
  }, {
    title: 'Send the contract',
    desc: 'Generate docs and track signatures.',
    icon: FileSignature
  }, {
    title: 'Request the deposit',
    desc: 'Collect, track, and remind automatically.',
    icon: DollarSign
  }, {
    title: 'Mark the show truly confirmed',
    desc: 'Only once the required conditions are met.',
    icon: CheckCircle2
  }];
  return <div className="min-h-screen pt-32 pb-24" data-id="element-7287">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 mb-20 text-center" data-id="element-7288">
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-4xl md:text-5xl font-bold text-textMain mb-6 tracking-tight" data-id="element-7289">
          The deal is not done until the money and paperwork are real.
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="text-lg text-textMuted leading-relaxed" data-id="element-7290">
          Booker tracks offers, counters, approvals, contracts, signatures,
          deposits, and settlement milestones in one place — so nobody confuses
          "interested" with "confirmed."
        </motion.p>
      </section>

      {/* Problem Section */}
      <section className="max-w-4xl mx-auto px-6 mb-20" data-id="element-7291">
        <div className="bg-red-500/5 border border-red-500/10 rounded-2xl p-8 md:p-12 text-center" data-id="element-7292">
          <h2 className="text-2xl font-bold text-textMain mb-4" data-id="element-7293">
            Most booking mistakes happen in the messy middle.
          </h2>
          <p className="text-textMuted leading-relaxed" data-id="element-7294">
            A venue says yes, terms change, the contract still needs to go out,
            the deposit is late, and everyone starts acting like the date is
            real anyway. Booker fixes that by turning the middle into a visible
            workflow.
          </p>
        </div>
      </section>

      {/* Scenario Story */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7295">
        <div className="bg-surface border border-border rounded-2xl p-8 md:p-12 relative overflow-hidden" data-id="element-7296">
          <div className="absolute top-0 left-0 w-1 h-full bg-primary" data-id="element-7297"></div>
          <div className="prose prose-invert max-w-none" data-id="element-7298">
            <p className="italic text-lg text-textMuted leading-relaxed mb-6" data-id="element-7299">
              A venue says yes, but they want different terms. The artist wants
              a deposit. The contract still has to go out, and nobody wants to
              lose the date because one detail slipped through email.
            </p>
            <p className="italic text-lg text-textMuted leading-relaxed" data-id="element-7300">
              Booker turns the messy middle into a structured workflow: offer,
              counter, approval, contract, signature, deposit, locked show.
            </p>
          </div>
        </div>
      </section>

      {/* Workflow Steps */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7301">
        <div className="space-y-6" data-id="element-7302">
          {steps.map((step, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="flex items-start gap-6 bg-surface border border-border rounded-2xl p-6" data-id="element-7303">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0" data-id="element-7304">
                <step.icon className="w-6 h-6 text-primary" data-id="element-7305" />
              </div>
              <div data-id="element-7306">
                <h3 className="text-xl font-bold text-textMain mb-2" data-id="element-7307">
                  Step {i + 1} — {step.title}
                </h3>
                <p className="text-textMuted" data-id="element-7308">{step.desc}</p>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* Outcomes */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7309">
        <div className="bg-surface border border-border rounded-2xl p-8 md:p-12 text-center" data-id="element-7310">
          <h2 className="text-2xl font-bold text-textMain" data-id="element-7311">
            Fewer "we thought that was booked" disasters.
          </h2>
        </div>
      </section>

      {/* CTA Band */}
      <section className="max-w-4xl mx-auto px-6 text-center" data-id="element-7312">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-7313">
          <Link to="/demo" className="px-8 py-3 bg-primary text-background font-bold rounded-lg hover:bg-primaryHover transition-colors" data-id="element-7314">
            See Booker in action
          </Link>
          <Link to="/demo" className="px-8 py-3 bg-surface border border-border text-textMain font-medium rounded-lg hover:bg-surfaceHover transition-colors" data-id="element-7315">
            Book a demo
          </Link>
        </div>
      </section>
    </div>;
}